import React, { useEffect, useRef, useState } from "react"
import { Row, Col } from "reactstrap"
import { useSelector, connect } from "react-redux"
import axios from "axios"
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth"
import config from "config/config"
import { format } from "date-fns"
import { useParams } from "react-router-dom"

import SnackbarComponent from "components/SnackBar"
import { setBreadcrumbItems } from "../../store/actions"
import Miniwidgets from "./MiniWidgets"
import DoctorSentimentChart from "./DoctorSentimentChart"
import SurveyDashboard from "./SurveyChart"
import PerformanceMetricsChart from "./CategoryScore"

const DoctorAnalysis = props => {
  // 👇 allow id from props OR route param
  const { id: propId } = props
  const params = useParams()
  const routeId = params?.id
  const doctorId = propId || routeId

  const activeOrganization = useSelector(
    state => state.organizationReducer.activeOrganization,
  )
  const userData = useSelector(state => state.Login.userData)

  const [loading, setLoading] = useState(false)
  const [doctor, setDoctor] = useState(null)
  const [fromDate, setFromDate] = useState("")
  const [toDate, setToDate] = useState("")
  const [sentimentByDate, setSetimentByDate] = useState([])
  const [doctorCategoryScore, setDoctorCategoryScore] = useState([])
  const [doctorSurveyVolume, setDoctorSurveyVolume] = useState([])
  const [err, setErr] = useState(null)
  const [success, setSuccess] = useState(null)
  const [reports, setReports] = useState([])
  const [viewMode, setViewMode] = useState("Day")

  document.title = "Dashboard | Reputation Management"
  const mode = useSelector(state => state.Layout.layoutMode)

  const formatDateDisplay = dateStr => {
    if (!dateStr) return "dd/mm/yyyy"
    const date = new Date(dateStr)
    const day = String(date.getDate()).padStart(2, "0")
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const year = date.getFullYear()
    return `${day}/${month}/${year}`
  }

  const fromDateRef = useRef(null)
  const toDateRef = useRef(null)

  const fetchAnalysis = async () => {
    if (!doctorId) return // nothing to fetch if no id

    try {
      setLoading(true)

      const response = await axios.get(
        `${config.backendUrl}/doctors/analysis`,
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
          },
          params: {
            doctorId,
            fromDate: fromDate || undefined,
            toDate: toDate || undefined,
            viewMode: viewMode || undefined,
          },
        },
      )

      console.log("Response", response)

      setDoctor(response.data.doctor)
      setSetimentByDate(response.data.analysis.sentimentByDate)
      setDoctorCategoryScore(response.data.analysis.categoryScores)
      setDoctorSurveyVolume(response.data.analysis.doctorSurveyVolume)
      setReports([
        {
          title: "Athena Patient Id",
          iconClass: "account-circle-outline",
          total: response.data.doctor.athenaDoctorId,
          badgecolor: "info",
        },
        {
          title: "Google Review Link",
          iconClass: "link-variant",
          total: response.data.doctor.googleReviewLink,
          badgecolor: "success",
        },
        {
          title: "Sentiment Score",
          iconClass: "emoticon-happy-outline",
          total: response.data.analysis.sentimentScore,
          badgecolor: "warning",
        },
        {
          title: "Customer Surveys",
          iconClass: "clipboard-text-outline",
          total: response.data.analysis.totalSurveys,
          badgecolor: "primary",
        },
      ])
    } catch (err) {
      console.log(err)
      setErr(err.response?.data?.message || "Error loading doctor analysis.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (doctorId) {
      fetchAnalysis()
    }
  }, [doctorId]) // you can also add fromDate/toDate/viewMode if you want auto-refetch

  if (!doctorId) {
    // Safe fallback if someone mounts this without id in props or route
    return (
      <div style={{ padding: 20 }}>
        <p>No doctor selected. Please provide an id prop or route param.</p>
      </div>
    )
  }

  return (
    <React.Fragment>
      <SnackbarComponent
        err={err}
        success={success}
        setErr={setErr}
        setSuccess={setSuccess}
      />

      <Miniwidgets noFilter reports={reports} loading={loading} />

      <div
        className="br"
        style={{
          backgroundColor: mode === "dark" ? "#252f40" : "#fff",
          marginTop: "0px",
          padding: "20px 20px",
          fontFamily: "Inter",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          color: mode === "dark" ? "#fff" : "#050409",
          fontWeight: "600",
          fontSize: "23px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "20px",
        }}
      >
        {/* Left side: doctor name */}
        <h1>
          {doctor?.firstName} {doctor?.lastName}
        </h1>

        {/* Right side: date inputs */}
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              backgroundColor: mode === "dark" ? "#1a2332" : "#F9FAFB",
              border:
                mode === "dark" ? "1px solid #374151" : "1px solid #E5E7EB",
              borderRadius: "8px",
              padding: "8px 12px",
              minWidth: "280px",
              gap: "8px",
            }}
          >
            {/* From Date */}
            <div
              style={{ position: "relative", flex: 1, cursor: "pointer" }}
              onClick={() => fromDateRef.current?.showPicker()}
            >
              <input
                ref={fromDateRef}
                type="date"
                value={fromDate}
                onChange={e => setFromDate(e.target.value)}
                style={{
                  position: "absolute",
                  inset: 0,
                  opacity: 0,
                  pointerEvents: "none",
                }}
              />
              <span
                style={{
                  color: fromDate
                    ? mode === "dark"
                      ? "#fff"
                      : "#374151"
                    : "#9CA3AF",
                  fontSize: "14px",
                }}
              >
                {fromDate ? formatDateDisplay(fromDate) : "dd/mm/yyyy"}
              </span>
            </div>

            <span style={{ color: "#9CA3AF" }}>-</span>

            {/* To Date */}
            <div
              style={{ position: "relative", flex: 1, cursor: "pointer" }}
              onClick={() => toDateRef.current?.showPicker()}
            >
              <input
                ref={toDateRef}
                type="date"
                value={toDate}
                onChange={e => setToDate(e.target.value)}
                style={{
                  position: "absolute",
                  inset: 0,
                  opacity: 0,
                  pointerEvents: "none",
                }}
              />
              <span
                style={{
                  color: toDate
                    ? mode === "dark"
                      ? "#fff"
                      : "#374151"
                    : "#9CA3AF",
                  fontSize: "14px",
                }}
              >
                {toDate ? formatDateDisplay(toDate) : "dd/mm/yyyy"}
              </span>
            </div>

            <CalendarMonthIcon
              size={18}
              style={{
                color: mode === "dark" ? "#9CA3AF" : "#6B7280",
                flexShrink: 0,
              }}
            />
          </div>
        </div>
      </div>

      <Row style={{ marginBottom: "20px" }}>
        <Col xl="12">
          <SurveyDashboard
            viewMode={viewMode}
            setViewMode={setViewMode}
            doctorSurveyVolume={doctorSurveyVolume}
            refetch={fetchAnalysis}
          />
        </Col>
      </Row>

      <Row>
        <Col xl="12" style={{ marginBottom: "20px" }}>
          <PerformanceMetricsChart categoryScores={doctorCategoryScore} />
        </Col>
      </Row>

      <Row>
        <Col xl="12">
          <DoctorSentimentChart sentimentByDate={sentimentByDate} />
        </Col>
      </Row>
    </React.Fragment>
  )
}

export default connect(null, { setBreadcrumbItems })(DoctorAnalysis)
